import React, {useEffect, useState, useRef, useCallback} from 'react';
import {Button} from "react-bootstrap";
import {useTranslation} from "react-i18next";
import {requestAcceptOffer, requestGetOffer} from "../../api";
import {OfferVersionResponse} from "../../models";
import Loader from "../../../../common/components/Loader";
import {getAxiosErrorMessage} from "../../../../common/utils/functions";
import {addNotify} from "../../../../common/actions/notify";
import {clearNeedAcceptOffer} from "../../../../common/actions/appStatus";
import {useDispatch} from "react-redux";
import styles from "./OfferScreen.module.scss";

type OfferScreenProps = {
  show: boolean;
  onAccepted: () => void;
  onRemindLater: () => void;
  onClose: () => void;
  viewOnly?: boolean;
}

const LANGUAGES = [
  {code: 'ru' as const, label: 'RU'},
  {code: 'kk' as const, label: 'KK'},
];

const OfferScreen: React.VFC<OfferScreenProps> = ({
  show,
  onAccepted,
  onRemindLater,
  onClose,
  viewOnly = false,
}) => {
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const acceptAreaRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);

  const [offer, setOffer] = useState<OfferVersionResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [lang, setLang] = useState<'ru' | 'kk'>('ru');
  const [isLangDropdownOpen, setIsLangDropdownOpen] = useState(false);
  const [isAcceptAreaVisible, setIsAcceptAreaVisible] = useState(false);

  const showOnlyCloseButton = viewOnly;

  const loadOffer = (language: 'ru' | 'kk') => {
    setLoading(true);
    requestGetOffer(language)
      .then(({data}) => {
        setOffer(data);
      })
      .catch(err => {
        dispatch(addNotify(getAxiosErrorMessage(err)));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (show && !offer) {
      loadOffer(lang);
    }
  }, [show]);

  useEffect(() => {
    if (show) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [show]);

  // Close language dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsLangDropdownOpen(false);
      }
    };

    if (isLangDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isLangDropdownOpen]);

  // Track accept button visibility for floating button via scroll
  useEffect(() => {
    const body = bodyRef.current;
    const acceptArea = acceptAreaRef.current;
    if (!body || !acceptArea || showOnlyCloseButton) return;

    const checkVisibility = () => {
      const bodyRect = body.getBoundingClientRect();
      const acceptRect = acceptArea.getBoundingClientRect();
      setIsAcceptAreaVisible(
        acceptRect.height > 0 &&
        acceptRect.top < bodyRect.bottom &&
        acceptRect.bottom > bodyRect.top
      );
    };

    body.addEventListener('scroll', checkVisibility);
    const rafId = requestAnimationFrame(checkVisibility);

    return () => {
      body.removeEventListener('scroll', checkVisibility);
      cancelAnimationFrame(rafId);
    };
  }, [show, offer, loading, showOnlyCloseButton]);

  const handleLanguageChange = (newLang: 'ru' | 'kk') => {
    setIsLangDropdownOpen(false);
    if (newLang !== lang) {
      setLang(newLang);
      loadOffer(newLang);
    }
  };

  const currentLangLabel = LANGUAGES.find(l => l.code === lang)?.label || 'RU';

  const handleAccept = () => {
    setAccepting(true);
    requestAcceptOffer()
      .then(() => {
        dispatch(clearNeedAcceptOffer());
        onAccepted();
      })
      .catch(err => {
        dispatch(addNotify(getAxiosErrorMessage(err)));
      })
      .finally(() => {
        setAccepting(false);
      });
  };

  const handleBack = () => {
    if (showOnlyCloseButton) {
      onClose();
    } else {
      onRemindLater();
    }
  };

  const scrollToAcceptArea = useCallback(() => {
    acceptAreaRef.current?.scrollIntoView({behavior: 'smooth'});
  }, []);

  if (!show) {
    return null;
  }

  const modalTitle = viewOnly
    ? t('offer.modalTitleView')
    : t('offer.screenTitle');

  return (
    <div className={styles.screen}>
      {/* Header: back arrow + centered title + language selector */}
      <div className={styles.header}>
        <button
          className={styles.backButton}
          onClick={handleBack}
          type="button"
          aria-label={t('common.actionClose')}
        >
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"
              fill="#000"
            />
          </svg>
        </button>

        <h1 className={styles.headerTitle}>{modalTitle}</h1>

        <div className={styles.langSelector} ref={dropdownRef}>
          <button
            type="button"
            className={styles.langDropdown}
            onClick={() => setIsLangDropdownOpen(!isLangDropdownOpen)}
          >
            {currentLangLabel}
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M7 10l5 5 5-5H7z" fill="#000" />
            </svg>
          </button>
          {isLangDropdownOpen && (
            <div className={styles.langDropdownMenu}>
              {LANGUAGES.map((language) => (
                <button
                  key={language.code}
                  type="button"
                  className={`${styles.langOption} ${lang === language.code ? styles.active : ''}`}
                  onClick={() => handleLanguageChange(language.code)}
                >
                  {language.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {loading ? (
        <div className={styles.loader}>
          <Loader />
        </div>
      ) : offer ? (
        <>
          <div className={styles.body} ref={bodyRef}>
            <div className={styles.contentArea}>
              {/* Changes section (if available and not view-only) */}
              {offer.changes && !viewOnly && (
                <div className={styles.changesSection}>
                  <div className={styles.changesTitle}>{t('offer.changesTitle')}</div>
                  <div dangerouslySetInnerHTML={{__html: offer.changes}} />
                </div>
              )}

              {/* Offer text */}
              <div
                className={styles.offerText}
                dangerouslySetInnerHTML={{__html: offer.text}}
              />
            </div>

            {/* Accept button area at the end of scrollable content */}
            {!showOnlyCloseButton && (
              <>
                <div className={styles.acceptArea} ref={acceptAreaRef}>
                  <button
                    className={styles.acceptButton}
                    onClick={handleAccept}
                    disabled={accepting}
                    type="button"
                  >
                    {t('offer.acceptButton')}
                  </button>
                  <button
                    className={styles.remindLaterButton}
                    onClick={onRemindLater}
                    disabled={accepting}
                    type="button"
                  >
                    {t('offer.remindLater')}
                  </button>
                  <span className={styles.acceptHint}>
                    {t('offer.acceptHint')}
                  </span>
                </div>
                <div className={styles.footerDivider} />
              </>
            )}
          </div>

          {/* View-only footer */}
          {showOnlyCloseButton && (
            <div className={styles.viewOnlyFooter}>
              <Button
                variant="outline-secondary"
                onClick={onClose}
              >
                {t('common.actionClose')}
              </Button>
            </div>
          )}

          {/* Floating accept button (hidden when accept area is visible) */}
          {!showOnlyCloseButton && (
            <button
              type="button"
              className={`${styles.floatingButton} ${isAcceptAreaVisible ? styles.hidden : ''}`}
              onClick={scrollToAcceptArea}
            >
              {t('offer.acceptButton')}
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M7 10l5 5 5-5H7z" fill="#fff" />
              </svg>
            </button>
          )}
        </>
      ) : null}
    </div>
  );
};

export default OfferScreen;
